"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { useClickOutside } from "@/hooks/useClickOutside";
import SelectList, { HandleChange, List } from "@/ui/Common/SelectList";
import { cn } from "@/utils/cn";

import styles from "./style.module.scss";

interface Props {
  current: string;
  list: List[];
  handleChange: HandleChange;
  withOverFull?: boolean;
  headIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
  withListIcon?: boolean;
  withSearch?: boolean;
  style?: any;
  className?: string;
  resizeWrapper?: boolean;
  alwaysOpen?: boolean;
  isOnlySymbol?: boolean;
  arrowColor?: string;
  type?: "header_desktop" | undefined;
  itemIconStyles?: string;
  isWhite?: boolean;
}

const Select: React.FC<Props> = ({
  current,
  list,
  handleChange,
  withListIcon = false,
  withOverFull = false,
  withSearch = false,
  style,
  className,
  headIcon: Icon,
  alwaysOpen,
  isOnlySymbol = false,
  arrowColor = "red",
  itemIconStyles = "icon",
  type,
  isWhite,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(alwaysOpen || false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleOpenList = useCallback(() => {
    setIsOpen((prev) => !prev);
    setSearchTerm("");
  }, []);

  const closeList = useCallback(() => {
    if (!alwaysOpen) {
      setIsOpen(false);
      setSearchTerm("");
    }
  }, [alwaysOpen]);

  const wrapperRef = useClickOutside(closeList);

  useEffect(() => {
    if (alwaysOpen) {
      setIsOpen(true);
    }
  }, [alwaysOpen]);

  const filteredList = list.filter((item) =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const CurrentIcon = list.find((item) => item.label === current)?.icon;
  return (
    <div
      ref={wrapperRef}
      className={cn(
        className,
        styles.wrapper,
        type && styles[`wrapper_${type}`],
        arrowColor === "noArrow" && styles.noArrow_wrapper,
        isOpen && type === "header_desktop" && styles.open_red,
        isWhite && "!bg-transparent !border-white !text-white",
      )}
      onClick={handleOpenList}
    >
      <div
        className={cn(styles.container, type && styles[`container_${type}`])}
      >
        <div
          className={cn(
            styles.current,
            arrowColor === "black" ? styles.arrowBlack : "",
            isOpen &&
              type === "header_desktop" &&
              arrowColor !== "noArrow" &&
              styles.arrowWhite,
            arrowColor === "noArrow" ? styles.noArrow : "",
            isWhite && !isOpen && styles.isWhite,
          )}
        >
          {withSearch && isOpen ? (
            <input
              type="text"
              className={styles.searchInput}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              autoFocus
            />
          ) : Icon || CurrentIcon ? (
            <div className={styles.iconContainer}>
              {CurrentIcon && type === "header_desktop" && (
                <CurrentIcon className={itemIconStyles} />
              )}
              {Icon && <Icon className={itemIconStyles} />}
              {!isOnlySymbol && current}
            </div>
          ) : (
            current
          )}
        </div>
        <SelectList
          ref={listRef}
          isOpen={isOpen}
          list={filteredList}
          handleChange={handleChange}
          withIcon={withListIcon}
          withOverFull={withOverFull}
          isOnlySymbol={isOnlySymbol}
          itemIconStyles={itemIconStyles}
          type={type}
        />
      </div>
    </div>
  );
};
export default Select;
